import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Alert,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import * as Yup from "yup";

import { ActivityButton, SequenceNumberInput } from "../../components/Common";
import { CategoriesTypeSelect } from "../../components";
import { CreateCategoryRequest } from "../../interfaces/category";
import {
  useGetCategoryQuery,
  useAddCategoryMutation,
  useUpdateCategoryMutation,
} from "../../store/api/categoryApi";
import { ErrorHandlingTypeEnum, SequenceEnum } from "../../enums";
import { ParsedError } from "../../interfaces";
import { ToastContainer } from "react-toastify";

type TProps = {
  id?: string | null;
  show: boolean;
  onCloseClick: (event: any) => void;
};
export const AddEditCategoryModal: React.FC<TProps> = ({
  id,
  show,
  onCloseClick,
}) => {
  const { t } = useTranslation();
  const [request] = useState<CreateCategoryRequest>();

  const {
    data: dataCategory,
    error: errorGetCategory,
    isSuccess: isSuccessGetCategory,
    isLoading: isLoadingGetCategory,
  } = useGetCategoryQuery(id!, { skip: !id });

  const [
    addCategory,
    { isLoading: isLoadingAdd, error: errorAdd, isError: isErrorAdd },
  ] = useAddCategoryMutation();

  const [
    updateCategoryMutate,
    { isLoading: isLoadingUpdate, error: errorUpdate, isError: isErrorUpdate },
  ] = useUpdateCategoryMutation();

  //#region form
  const form = useFormik<CreateCategoryRequest>({
    enableReinitialize: true,
    initialValues: {
      name: {
        en: request?.name.en || "",
        ar: request?.name.ar || "",
      },
      type: request?.type || "",
      sequenceNumber: request?.sequenceNumber || 0,
    },
    validationSchema: Yup.object({
      name: Yup.object({
        en: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "englishValue",
            }),
          ),
        ar: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "arabicValue",
            }),
          ),
      }),
      type: Yup.string().required(
        t("isNotEmpty", {
          displayName: "type",
        }),
      ),
      sequenceNumber: Yup.number().required(
        t("isNotEmpty", {
          displayName: "sequenceNumber",
        }),
      ),
    }),
    onSubmit: async (values) => {
      try {
        if (id) {
          await updateCategoryMutate({ id, data: values }).unwrap();
        } else {
          await addCategory(values).unwrap();
        }
        onClose();
      } catch (e) {}
    },
  });
  //#endregion

  //#region handlers
  const onClose = () => {
    form.resetForm();
    onCloseClick(!show);
  };

  useEffect(() => {
    if (id && dataCategory) {
      form.setValues({
        name: dataCategory.name,
        type: dataCategory.type,
        sequenceNumber:
          form.values.sequenceNumber || dataCategory.sequenceNumber || 0,
      });
    }
  }, [dataCategory, isLoadingGetCategory, isSuccessGetCategory, id]);

  useEffect(() => {
    let _error: ParsedError | null = null;
    if (!isLoadingAdd && isErrorAdd) _error = errorAdd as ParsedError;
    if (!isLoadingUpdate && isErrorUpdate) _error = errorUpdate as ParsedError;
    if (_error?.type == ErrorHandlingTypeEnum.FORM) {
      form.setErrors(_error.error);
    }
  }, [isLoadingAdd, isLoadingUpdate]);

  //#endregion

  return (
    <React.Fragment>
      <Modal
        id="addEdit"
        isOpen={show}
        toggle={() => {
          onClose();
        }}
        centered
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
            return false;
          }}
          action="#"
        >
          <ModalHeader
            className="p-3"
            toggle={() => {
              onClose();
            }}
          >
            {id
              ? t("editItem", { item: "category" })
              : t("addItem", { item: "category" })}
          </ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <Label htmlFor="type" className="form-label">
                {t("type")}
                <span className="text-danger">*</span>
              </Label>
              <CategoriesTypeSelect
                type="select"
                id="type"
                name="type"
                className="form-select"
                aria-label={t("type")}
                placeholder={t("type")}
                value={form.values.type}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={form.touched.type && form.errors.type ? true : false}
              ></CategoriesTypeSelect>
              {form.touched.type && form.errors.type ? (
                <FormFeedback type="invalid">{form.errors.type}</FormFeedback>
              ) : null}
            </div>
            <SequenceNumberInput
              tabIndex={1}
              value={form.values.sequenceNumber}
              onChange={(e) => {
                e.preventDefault();
                const { value } = e.target;
                const regex =
                  /^(0*[0-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
                if (regex.test(value.toString())) {
                  form.setFieldValue("sequenceNumber", value);
                } else {
                  form.setFieldValue("sequenceNumber", "");
                }
              }}
              onBlur={form.handleBlur}
              invalid={
                form.touched.sequenceNumber && form.errors.sequenceNumber
                  ? true
                  : false
              }
              sequenceType={SequenceEnum.FLASH_CARD}
              onGenerateSequenceNumber={(v: number) => {
                form.setFieldValue("sequenceNumber", v);
              }}
              mode={id ? "edit" : "add"}
              request={{
                type: SequenceEnum.CATEGORY,
                otherType: form.values.type,
              }}
            />
            {form.touched.sequenceNumber && form.errors.sequenceNumber ? (
              <FormFeedback type="invalid">
                {form.errors.sequenceNumber as string}
              </FormFeedback>
            ) : null}
            <div className="mb-3">
              <label htmlFor="name.en" className="form-label">
                {t("nameEn")}
                <span className="text-danger">*</span>
              </label>
              <Input
                disabled={!!errorGetCategory}
                autoComplete="off"
                id="name.en"
                name="name.en"
                value={form.values?.name.en}
                type="text"
                className="form-control"
                placeholder={t("enterInEnglish")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.name?.en && form.errors.name?.en ? true : false
                }
              />
              {form.touched.name?.en && form.errors.name?.en ? (
                <FormFeedback type="invalid">
                  {form.errors.name?.en as string}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="name.ar" className="form-label">
                {t("nameAr")}
                <span className="text-danger">*</span>
              </label>
              <Input
                style={{ fontFamily: "aljannat", direction: "rtl" }}
                disabled={!!errorGetCategory}
                autoComplete="off"
                auto
                id="name.ar"
                name="name.ar"
                value={form.values.name?.ar}
                type="text"
                className="form-control"
                placeholder={t("enterInArabic")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.name?.ar && form.errors.name?.ar ? true : false
                }
              />
              {form.touched.name?.ar && form.errors.name?.ar ? (
                <FormFeedback type="invalid">
                  {form.errors.name?.ar as string}
                </FormFeedback>
              ) : null}
            </div>
          </ModalBody>
          <ModalFooter>
            <Link
              to={"#"}
              onClick={onClose}
              className="btn btn-link link-success fw-medium"
            >
              <i className="ri-close-line me-1 align-middle"></i> {t("close")}
            </Link>
            <ActivityButton
              title="save"
              inProgressTitle="saving"
              color="success"
              className="btn btn-success"
              type="submit"
              inProgress={isLoadingAdd || isLoadingUpdate}
              disabled={isLoadingAdd || isLoadingUpdate || !!errorGetCategory}
            ></ActivityButton>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
};
